var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../BlocksElement';
import '../skeleton/skeleton';
import styles from './badge.styles';
/**
 * @slot before - The Badge before
 * @csspart base - The component's base wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart part-text - The component's text.
 * @csspart badge-text - The component's  badge text.
 * @csspart label - The component's label.
 * @csspart skeleton - The component's skeleton wrapper.
 * @csspart loader - The component's loader wrapper for isDot.
 */
export class BlocksBadge extends BlocksElement {
    constructor() {
        super(...arguments);
        /**
         * Badge variant
         */
        this.variant = 'blue';
        /**
         * Size variant
         * <cat-text-passage size="sm">
         * <ul>
         * <li>**sm** decreases overall size for badge with 16px height</li>
         * </ul>
         * </cat-text-passage>
         */
        this.size = 'md';
    }
    static { this.styles = styles; }
    /**
     * First updated lifecycle hook
     * 1) Handle the position of the parent element
     */
    firstUpdated() {
        this.handlePosition();
    }
    /**
     * Handle position
     * 1) If a position is defined, then apply position: relative to the direct parent element
     */
    handlePosition() {
        if (this.position && this.parentElement) {
            this.parentElement.style.position = 'relative';
            this.parentElement.style.width = 'fit-content';
        }
    }
    renderLoader(componentClassNames) {
        return html `
      <div part="base" class="${componentClassNames}">
        ${this.size === 'sm'
            ? html ` <cat-skeleton part="skeleton" exportparts=${this.getExportParts('skeleton')} size="sm" width="39px"></cat-skeleton> `
            : html `<cat-skeleton part="skeleton" exportparts=${this.getExportParts('skeleton')} width="44px"></cat-skeleton>`}
      </div>
    `;
    }
    render() {
        const componentClassNames = this.componentClassNames('cat-c-badge', {
            'cat-c-badge--blue': this.variant === 'blue' && !this.isLoading,
            'cat-c-badge--green': this.variant === 'green',
            'cat-c-badge--grey': this.variant === 'grey',
            'cat-c-badge--yellow': this.variant === 'yellow',
            'cat-c-badge--brand': this.variant === 'brand',
            'cat-c-badge--orange': this.variant === 'orange',
            'cat-c-badge--red': this.variant === 'red',
            'cat-c-badge--contrast': this.variant === 'contrast',
            'cat-c-badge--disabled': this.variant === 'disabled',
            'cat-is-dot': this.isDot,
            'cat-c-badge--sm': this.size === 'sm',
            'cat-c-badge--top-left': this.position === 'top-left',
            'cat-c-badge--top-right': this.position === 'top-right',
            'cat-is-loading': this.isLoading
        });
        if (this.isLoading && !this.isDot) {
            return this.renderLoader(componentClassNames);
        }
        return html `
      <div part="base" class="${componentClassNames}" aria-label="${ifDefined(this.arialabel)}">
        ${html `<slot part="part-before" name="before"></slot>`}
        ${!this.isDot && this.text ? html `<span part="badge-text" class="cat-c-text">${this.text}</span>` : nothing}
      </div>
      ${this.isDot && this.isLoading
            ? html `<span part="loader" class="cat-dot-loader"
            ><cat-skeleton part="skeleton" exportparts=${this.getExportParts('skeleton')} size="sm" width="39px"></cat-skeleton
          ></span>`
            : this.isDot && this.text
                ? html `<span part="label" class="cat-dot-optional-label">${this.text}</span>`
                : nothing}
    `;
    }
}
__decorate([
    property({ type: String, reflect: true })
], BlocksBadge.prototype, "text", void 0);
__decorate([
    property({ type: Boolean })
], BlocksBadge.prototype, "isLoading", void 0);
__decorate([
    property({ type: Boolean })
], BlocksBadge.prototype, "isDot", void 0);
__decorate([
    property({ type: String, reflect: true })
], BlocksBadge.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], BlocksBadge.prototype, "size", void 0);
__decorate([
    property({ type: String })
], BlocksBadge.prototype, "position", void 0);
__decorate([
    property({ type: String, reflect: true })
], BlocksBadge.prototype, "arialabel", void 0);
if (customElements.get('cat-badge') === undefined) {
    customElements.define('cat-badge', BlocksBadge);
}
